"use client";

import { useEffect, useState } from "react";
import { CONSENT_EVENT, CONSENT_KEY, CONSENT_OPEN_EVENT } from "@/config/legal";

/* Single reader for the cookie/tracking choice.
 *
 * - "accepted" only after the visitor clicks Accept.
 * - Global Privacy Control (or Do Not Track set to 1) is treated as a standing Decline.
 * - Anything else (no choice yet, storage blocked) is "none": optional scripts stay OFF. */

export type ConsentChoice = "accepted" | "declined" | null;

export function gpcOn(): boolean {
  if (typeof navigator === "undefined") return false;
  const n = navigator as Navigator & { globalPrivacyControl?: boolean };
  return n.globalPrivacyControl === true;
}

export function readConsent(): ConsentChoice {
  if (typeof window === "undefined") return null;
  if (gpcOn()) return "declined";
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    return v === "accepted" || v === "declined" ? v : null;
  } catch {
    return null;
  }
}

export function writeConsent(choice: "accepted" | "declined") {
  try {
    window.localStorage.setItem(CONSENT_KEY, choice);
  } catch {
    /* choice just will not persist */
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT));
}

export function openConsentSettings() {
  window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}

/** True only when the visitor has explicitly accepted optional scripts. */
export function useOptionalScriptsAllowed(): boolean {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    const sync = () => setOk(readConsent() === "accepted");
    sync();
    window.addEventListener(CONSENT_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CONSENT_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return ok;
}
